/* ============================================================================
   CART PERF (assets/cart-perf.js) — Stage 1: drawer quantity mutations
   ----------------------------------------------------------------------------
   Makes drawer quantity edits optimistic, debounced, and single-round-trip
   (docs/cart-network-audit.md has the full trace this responds to):

   - Capture-phase interception of [data-action=increase/decrease-quantity]
     clicks INSIDE #cart-drawer, before theme.js's bubble-phase delegate sees
     them. Everything else (add-to-cart, the /cart page, markup without
     data-key) still flows through the stock theme.js path unchanged.
   - The visible quantity updates instantly; rapid clicks settle (280 ms)
     into ONE POST /cart/change.js with the final amount. This also fixes the
     stock absolute-data-quantity race where clicks landing before a
     re-render all posted the same stale value.
   - Mutations address lines by line-item KEY (data-key, added in
     cart-drawer-line-item.liquid) — precise for duplicate variants (e.g.
     designer cups with distinct properties) and immune to index shifts.
   - `sections: "cart-drawer"` rides in the POST body, so the response
     carries the freshly rendered drawer HTML: zero follow-up section GETs.
     The HTML is handed to cart-drawer.js via `cartperf:drawer-html`, which
     applies it with its usual pipeline (app-node preservation, T&C restore,
     upsells, `cart:rerendered` re-announce for apps).
   - Flushes are serialized so responses can't apply stale HTML out of order.
   - Line keys are NOT stable on this store: the tier-pricing discount
     re-keys a line whenever it re-evaluates. Pending edits therefore track
     variant + line position too, are re-pointed at the fresh key after every
     re-render, and a 400 "no valid id" is recovered from /cart.js and
     retried once instead of silently dropping the shopper's click.
   - Checkout / page exit never outruns a pending edit: checkout submits wait
     for the queue to drain, and pagehide sends anything still debounced with
     keepalive.
   - Inventory caps (422) surface Shopify's own message in a polite live
     region; on any failure the drawer falls back to its normal full refresh,
     so the server's truth always wins.
   ========================================================================== */
(function () {
  "use strict";
  if (!window.fetch || !window.Promise || !Element.prototype.closest) return;

  var DEBOUNCE_MS = 280;
  var pending = {};   /* slot -> { key, variant, line, qty, timer } */
  var busy = 0;       /* flushes queued or in flight */
  var queued = [];    /* flushed entries waiting their turn in the chain */
  var chain = Promise.resolve(); /* serializes flushes + ordered HTML application */

  function changeUrl() {
    return (window.routes && window.routes.cartChangeUrl ? window.routes.cartChangeUrl : "/cart/change") + ".js";
  }
  function cartUrl() {
    return window.routes && window.routes.cartUrl ? window.routes.cartUrl : "/cart";
  }

  function controlFor(target) {
    if (!target.closest) return null;
    var btn = target.closest('[data-action="increase-quantity"], [data-action="decrease-quantity"]');
    if (!btn) return null;
    if (!btn.hasAttribute("data-key")) return null; /* stale markup -> stock path */
    if (!btn.closest("#cart-drawer")) return null;  /* Stage 1: drawer only */
    return btn;
  }

  function rowOf(el) {
    return el.closest(".cart-drawer__line-item");
  }
  function rowInput(el) {
    var sel = el.closest(".quantity-selector");
    if (sel) return sel.querySelector(".quantity-selector__value");
    var row = rowOf(el);
    return row ? row.querySelector(".quantity-selector__value") : null;
  }

  /* the pending slot a control belongs to: same key, or same variant at the
     same line position after a re-key */
  function slotFor(key, line) {
    if (pending[key]) return key;
    var variant = String(key).split(":")[0];
    for (var slot in pending) {
      if (pending[slot].variant === variant && pending[slot].line === line) return slot;
    }
    return null;
  }

  function targetQty(btn, slot) {
    /* the row's remove ✕ is a decrease-quantity link outside the selector */
    if (!btn.closest(".quantity-selector")) {
      var abs = parseInt(btn.getAttribute("data-quantity"), 10);
      return isNaN(abs) ? 0 : abs;
    }
    var cur;
    if (slot && typeof pending[slot].qty === "number") {
      cur = pending[slot].qty;
    } else {
      var input = rowInput(btn);
      cur = input ? parseInt(input.value, 10) : NaN;
      if (isNaN(cur)) cur = 1;
    }
    return btn.getAttribute("data-action") === "increase-quantity" ? cur + 1 : Math.max(0, cur - 1);
  }

  function paint(input, qty) {
    if (!input) return;
    input.value = qty;
    input.setAttribute("data-current-value", qty);
    input.setAttribute("size", Math.max(String(qty).length, 2));
  }

  document.addEventListener("click", function (event) {
    var btn = controlFor(event.target);
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation(); /* theme.js never double-handles this click */
    var key = btn.getAttribute("data-key");
    var line = btn.getAttribute("data-line");
    var slot = slotFor(key, line);
    var qty = targetQty(btn, slot);
    paint(rowInput(btn), qty);
    if (qty === 0) {
      var row = rowOf(btn);
      if (row) row.style.opacity = "0.45"; /* removal feedback until the swap lands */
    }
    schedule(slot || key, key, line, qty);
  }, true);

  /* typed quantities: same flow, value taken as-is */
  document.addEventListener("change", function (event) {
    var input = event.target;
    if (!input.matches || !input.matches(".quantity-selector__value")) return;
    if (!input.closest("#cart-drawer")) return;
    var sel = input.closest(".quantity-selector");
    var keyEl = sel && sel.querySelector("[data-key]");
    if (!keyEl) return; /* stock path */
    event.stopPropagation();
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    input.value = qty;
    var key = keyEl.getAttribute("data-key");
    var line = keyEl.getAttribute("data-line");
    schedule(slotFor(key, line) || key, key, line, qty);
  }, true);

  function schedule(slot, key, line, qty) {
    if (pending[slot]) clearTimeout(pending[slot].timer);
    var entry = { key: key, variant: String(key).split(":")[0], line: line, qty: qty };
    entry.timer = setTimeout(function () { flush(slot); }, DEBOUNCE_MS);
    pending[slot] = entry;
  }

  function flush(slot) {
    var entry = pending[slot];
    if (!entry) return chain;
    clearTimeout(entry.timer);
    delete pending[slot];
    busy++;
    queued.push(entry);
    chain = chain
      .then(function () { return send(entry); })
      .then(done, done);
    return chain;
  }
  function done() { busy = Math.max(0, busy - 1); }

  function flushAll() {
    Object.keys(pending).forEach(flush);
    return chain;
  }

  /* after any re-render (ours, theme.js, apps): re-point pending edits at the
     fresh line keys and keep their optimistic quantities on screen */
  document.addEventListener("cart:rerendered", function () {
    Object.keys(pending).map(function (slot) { return pending[slot]; }).concat(queued).forEach(function (entry) {
      var ctl = document.querySelector('#cart-drawer .quantity-selector [data-key="' + entry.key + '"]');
      if (!ctl) {
        var candidates = document.querySelectorAll('#cart-drawer .quantity-selector [data-key^="' + entry.variant + ':"]');
        for (var i = 0; i < candidates.length; i++) {
          if (candidates[i].getAttribute("data-line") === entry.line) { ctl = candidates[i]; break; }
        }
        if (!ctl && candidates.length === 1) ctl = candidates[0];
      }
      if (!ctl) return;
      entry.key = ctl.getAttribute("data-key");
      entry.line = ctl.getAttribute("data-line");
      paint(rowInput(ctl), entry.qty);
    });
  });

  function post(entry, keepalive) {
    return fetch(changeUrl(), {
      method: "POST",
      credentials: "same-origin",
      keepalive: !!keepalive,
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(keepalive
        ? { id: entry.key, quantity: entry.qty }
        : { id: entry.key, quantity: entry.qty, sections: "cart-drawer", sections_url: cartUrl() })
    });
  }

  /* a stale key (re-keyed by a discount re-evaluation) -> find the line again */
  function rekeyFromCart(entry) {
    return fetch(cartUrl() + ".js", { credentials: "same-origin" })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var same = cart.items.filter(function (it) { return String(it.variant_id) === entry.variant; });
        var idx = parseInt(entry.line, 10) - 1;
        var hit = (cart.items[idx] && String(cart.items[idx].variant_id) === entry.variant) ? cart.items[idx]
                : (same.length === 1 ? same[0] : null);
        if (!hit) throw new Error("line gone");
        entry.key = hit.key;
        return entry;
      });
  }

  function send(entry, retried) {
    var q = queued.indexOf(entry);
    if (q !== -1) queued.splice(q, 1);
    return post(entry)
      .then(function (res) {
        if (res.status === 400 && !retried) {
          return rekeyFromCart(entry).then(function (e) { return send(e, true); });
        }
        return res.json().then(function (data) { return { res: res, data: data }; });
      })
      .then(function (out) {
        if (!out || !out.res) return; /* retried branch already handled */
        if (!out.res.ok) {
          if (out.res.status === 422) notify(out.data && (out.data.description || out.data.message));
          else if (out.res.status >= 500) notify("We couldn't update your cart. Please try again.");
          throw new Error("change.js " + out.res.status);
        }
        var cart = out.data;
        document.querySelectorAll(".header__cart-count").forEach(function (el) {
          el.textContent = cart.item_count;
        });
        var html = cart.sections && cart.sections["cart-drawer"];
        document.dispatchEvent(new CustomEvent("cartperf:drawer-html", { detail: { html: html || null } }));
      })
      .catch(function () {
        /* let the drawer re-fetch the truth */
        document.dispatchEvent(new CustomEvent("cartperf:drawer-html", { detail: { html: null } }));
      });
  }

  /* ---------- checkout + page exit never outrun a pending edit ---------- */

  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!form.closest || !form.closest("#cart-drawer")) return;
    if (form.__cartperfReleased) { form.__cartperfReleased = false; return; }
    if (!busy && !Object.keys(pending).length) return;
    event.preventDefault();
    var submitter = event.submitter || form.querySelector('[name="checkout"]');
    if (submitter) submitter.setAttribute("aria-busy", "true");
    var submitterName = submitter && submitter.name;
    flushAll().then(function () {
      /* the flush re-rendered the drawer: submit the live form, not the
         detached one the shopper clicked (a detached form never submits) */
      var live = form.isConnected ? form : (document.querySelector("#cart-drawer .cart-drawer__form") || form);
      var liveSubmitter = submitter && submitter.isConnected ? submitter
        : (submitterName ? live.querySelector('[name="' + submitterName + '"]') : null);
      live.__cartperfReleased = true;
      if (liveSubmitter) liveSubmitter.removeAttribute("aria-busy");
      if (live.requestSubmit) { live.requestSubmit(liveSubmitter || undefined); return; }
      if (submitterName) {
        var hidden = document.createElement("input");
        hidden.type = "hidden"; hidden.name = submitterName; hidden.value = (liveSubmitter && liveSubmitter.value) || "";
        live.appendChild(hidden);
      }
      live.submit();
    });
  }, true);

  window.addEventListener("pagehide", function () {
    Object.keys(pending).forEach(function (slot) {
      var entry = pending[slot];
      clearTimeout(entry.timer);
      delete pending[slot];
      try { post(entry, true); } catch (e) { /* best effort */ }
    });
  });

  /* ---------- shopper-facing messages (inventory caps, failures) ---------- */

  var noticeEl = null, noticeTimer = null;
  function notify(message) {
    if (!message) return;
    if (!noticeEl) {
      noticeEl = document.createElement("div");
      noticeEl.className = "cart-perf-notice";
      noticeEl.setAttribute("role", "status");
      noticeEl.setAttribute("aria-live", "polite");
      document.body.appendChild(noticeEl);
    }
    noticeEl.textContent = message;
    noticeEl.classList.add("is-visible");
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(function () { noticeEl.classList.remove("is-visible"); }, 6000);
  }
})();
